/**
 * CR315: Theme-Owned Keys Guard
 * 
 * Write-guard and read-guard utilities to prevent theme-owned styling keys
 * from being persisted in deal_card_templates.
 * 
 * Theme Labs is the single source of truth for:
 * - borderWidth
 * - cornerRadius
 * - cornerRadiusPreset
 * - shadowIntensity
 * 
 * These keys must never appear in template_json, legacy_template_json, or overrides_json.
 */

// ================================================================
// THEME-OWNED KEYS (must never be persisted in deal card templates)
// ================================================================

/**
 * Keys that are owned by Theme Labs and must not appear in deal card templates.
 * These control global card styling and are managed via Theme Labs → Components.
 */
export const THEME_OWNED_KEYS = [
  "borderWidth",
  "cornerRadius",
  "cornerRadiusPreset",
  "shadowIntensity",
] as const;

export type ThemeOwnedKey = typeof THEME_OWNED_KEYS[number];

/**
 * CR310: Legacy ribbon keys that must be stripped from V2 templates.
 * For schemaVersion=2, ribbon visibility is controlled SOLELY by
 * surfaceOverrides.primary.blockToggles.ribbon
 * 
 * These keys must be removed from:
 * - overrides_json.styleOverrides.showRibbon
 * - template_json.styling.showRibbon
 * - template_json.extended_sections.ribbon
 * - overrides_json.extended_sections.ribbon
 */
export const LEGACY_RIBBON_KEYS = [
  "showRibbon",
] as const;

export type LegacyRibbonKey = typeof LEGACY_RIBBON_KEYS[number];

// ================================================================
// WRITE-GUARD: Sanitizer for persistence
// ================================================================

/**
 * Recursively removes theme-owned keys from any JSON object.
 * Safe to call on any value - returns primitive values unchanged.
 * 
 * @param obj - The object to sanitize (can be any JSON value)
 * @returns A new object with theme-owned keys removed at all nesting levels
 */
export function sanitizeDealCardJsonForV2<T>(obj: T): T {
  // Handle null/undefined
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  // Handle primitives (string, number, boolean)
  if (typeof obj !== "object") {
    return obj;
  }
  
  // Handle arrays - recursively sanitize each element
  if (Array.isArray(obj)) {
    return obj.map((item) => sanitizeDealCardJsonForV2(item)) as T;
  }
  
  // Handle objects - remove theme-owned keys and recurse on remaining
  const sanitized: Record<string, unknown> = {};
  
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    // Skip theme-owned keys at any level
    if (THEME_OWNED_KEYS.includes(key as ThemeOwnedKey)) {
      continue;
    }
    
    // Recursively sanitize nested objects/arrays
    sanitized[key] = sanitizeDealCardJsonForV2(value);
  }
  
  return sanitized as T;
}

/**
 * Check if an object contains any theme-owned keys (at any nesting level).
 * Used for debugging and validation.
 * 
 * @param obj - The object to check
 * @returns Object with detection results
 */
export function detectThemeOwnedKeys(obj: unknown): {
  hasThemeOwnedKeys: boolean;
  keysFound: ThemeOwnedKey[];
  locations: string[];
} {
  const keysFound: Set<ThemeOwnedKey> = new Set();
  const locations: string[] = [];
  
  function traverse(value: unknown, path: string) {
    if (value === null || value === undefined || typeof value !== "object") {
      return;
    }
    
    if (Array.isArray(value)) {
      value.forEach((item, idx) => traverse(item, `${path}[${idx}]`));
      return;
    }
    
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      const currentPath = path ? `${path}.${key}` : key;
      
      if (THEME_OWNED_KEYS.includes(key as ThemeOwnedKey)) {
        keysFound.add(key as ThemeOwnedKey);
        locations.push(currentPath);
      }
      
      traverse(val, currentPath);
    }
  }
  
  traverse(obj, "");
  
  return {
    hasThemeOwnedKeys: keysFound.size > 0,
    keysFound: Array.from(keysFound),
    locations,
  };
}

// ================================================================
// READ-GUARD: Filter for resolution
// ================================================================

/**
 * Filter style overrides to remove theme-owned keys during resolution.
 * Theme Labs tokens always win - these keys should never come from deal card config.
 * 
 * @param styleOverrides - The style overrides to filter
 * @returns Style overrides with theme-owned keys removed
 */
export function filterThemeOwnedFromStyleOverrides(
  styleOverrides: Record<string, unknown>
): Record<string, unknown> {
  const filtered: Record<string, unknown> = {};
  
  for (const [key, value] of Object.entries(styleOverrides)) {
    if (!THEME_OWNED_KEYS.includes(key as ThemeOwnedKey)) {
      filtered[key] = value;
    }
  }
  
  return filtered;
}

// ================================================================
// CR310: LEGACY RIBBON SANITIZER
// ================================================================

/**
 * CR310: Strip legacy ribbon keys from JSON payloads before persistence.
 * For V2 templates, showRibbon is controlled solely by blockToggles.ribbon.
 * 
 * @param obj - The object to sanitize
 * @returns Object with legacy ribbon keys removed
 */
export function stripLegacyRibbonKeys<T>(obj: T): T {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj !== "object") return obj;
  if (Array.isArray(obj)) {
    return obj.map((item) => stripLegacyRibbonKeys(item)) as T;
  }
  
  const sanitized: Record<string, unknown> = {};
  
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    // Remove showRibbon from styleOverrides and styling
    if (key === "showRibbon") continue;
    
    // Recursively process nested objects, with special handling for known structures
    if (key === "styling" && typeof value === "object" && value !== null) {
      const stylingFiltered: Record<string, unknown> = {};
      for (const [sk, sv] of Object.entries(value as Record<string, unknown>)) {
        if (sk !== "showRibbon") {
          stylingFiltered[sk] = sv;
        }
      }
      if (Object.keys(stylingFiltered).length > 0) {
        sanitized[key] = stylingFiltered;
      }
      continue;
    }
    
    if (key === "extended_sections" && typeof value === "object" && value !== null) {
      const sectionsFiltered: Record<string, unknown> = {};
      for (const [sk, sv] of Object.entries(value as Record<string, unknown>)) {
        if (sk !== "ribbon") {
          sectionsFiltered[sk] = sv;
        }
      }
      if (Object.keys(sectionsFiltered).length > 0) {
        sanitized[key] = sectionsFiltered;
      }
      continue;
    }
    
    if (key === "styleOverrides" && typeof value === "object" && value !== null) {
      const overridesFiltered: Record<string, unknown> = {};
      for (const [sk, sv] of Object.entries(value as Record<string, unknown>)) {
        if (sk !== "showRibbon") {
          overridesFiltered[sk] = sv;
        }
      }
      if (Object.keys(overridesFiltered).length > 0) {
        sanitized[key] = overridesFiltered;
      }
      continue;
    }
    
    sanitized[key] = stripLegacyRibbonKeys(value);
  }
  
  return sanitized as T;
}

// ================================================================
// DEBUG INDICATOR
// ================================================================

/**
 * Analyze a deal card template row for theme-owned key presence.
 * Used by the V2 Debug Inspector.
 * 
 * @param templateJson - The template_json column value
 * @param legacyTemplateJson - The legacy_template_json column value
 * @param overridesJson - The overrides_json column value
 */
export function analyzeTemplateForThemeKeys(
  templateJson: unknown,
  legacyTemplateJson: unknown,
  overridesJson: unknown
): {
  themeKeysPresent: boolean;
  templateJsonKeys: ThemeOwnedKey[];
  legacyTemplateJsonKeys: ThemeOwnedKey[];
  overridesJsonKeys: ThemeOwnedKey[];
  sources: ("template_json" | "legacy_template_json" | "overrides_json")[];
} {
  const template = detectThemeOwnedKeys(templateJson);
  const legacy = detectThemeOwnedKeys(legacyTemplateJson);
  const overrides = detectThemeOwnedKeys(overridesJson);
  
  const sources: ("template_json" | "legacy_template_json" | "overrides_json")[] = [];
  if (template.hasThemeOwnedKeys) sources.push("template_json");
  if (legacy.hasThemeOwnedKeys) sources.push("legacy_template_json");
  if (overrides.hasThemeOwnedKeys) sources.push("overrides_json");
  
  return {
    themeKeysPresent: sources.length > 0,
    templateJsonKeys: template.keysFound,
    legacyTemplateJsonKeys: legacy.keysFound,
    overridesJsonKeys: overrides.keysFound,
    sources,
  };
}
